import { create } from "zustand";
import { doc, onSnapshot } from "firebase/firestore";
import { getSearchApiBaseUrl } from "../apiConfig";
import { getAuthHeader } from "./auth";
import { firebaseHelper } from "./firebase";

export interface CreditBalance {
  totalBalance: number;
  stripeBalance?: number;
  freeBalance?: number;
  cachedUnbilledUsage?: number;
  lastUpdated?: number | null;
  isLegacyUser?: boolean;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) return error.message || fallback;
  if (typeof error === "string") return error;
  return fallback;
}

interface CreditStoreState {
  balance: CreditBalance | null;
  teamBalance: CreditBalance | null;
  designFirmId: string | null;
  hideTeamBalance: boolean;
  isLoading: boolean;
  error: string | null;

  fetchBalance: (force?: boolean, uid?: string) => Promise<void>;
  fetchTeamBalance: (designFirmId: string, force?: boolean) => Promise<void>;
  checkSufficientCredits: (required: number) => boolean;
  subscribeToBalance: (uid: string) => void;
  subscribeToTeamBalance: (designFirmId: string) => void;
  unsubscribeAll: () => void;
  resetStore: () => void;
}

// Module-level unsubscribe references (not stored in Zustand state)
let personalUnsubscribe: (() => void) | null = null;
let teamUnsubscribe: (() => void) | null = null;

// UID-scoped dedup state for signup bonus check
let currentUid: string | null = null;
let bonusCheckedForUid: string | null = null;
let bonusInFlightUid: string | null = null;
let bonusInFlight: Promise<boolean> | null = null;

const DEFAULT_TIMEOUT_MS = 15000;

async function callCreditsApi<T>(
  path: string,
  options: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<T> {
  const base = getSearchApiBaseUrl();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(await getAuthHeader()),
    ...(options.headers as Record<string, string>),
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(`${base}${path}`, {
      ...options,
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Request failed: ${res.status}`);
    }

    return (await res.json()) as T;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error("Request timed out - server may be unavailable");
    }
    throw error;
  }
}

/**
 * Deduped signup bonus check. Shares in-flight requests for the same uid,
 * flags on success only (transient failures allow retry), and guards against
 * stale resolutions after resetStore or user switch.
 */
async function checkSignupBonus(uid: string): Promise<boolean> {
  if (!uid) return false;
  if (bonusCheckedForUid === uid) return false;
  if (bonusInFlight && bonusInFlightUid === uid) return bonusInFlight;

  bonusInFlightUid = uid;
  bonusInFlight = (async () => {
    try {
      const result = await callCreditsApi<{
        ok: boolean;
        granted: boolean;
      }>("/api/credits/allocate-signup-bonus", { method: "POST" });
      if (bonusInFlightUid === uid) {
        bonusCheckedForUid = uid;
      }
      return result.granted === true;
    } catch (err) {
      console.warn("[CreditStore] Signup bonus check failed:", err);
      return false;
    } finally {
      if (bonusInFlightUid === uid) {
        bonusInFlight = null;
        bonusInFlightUid = null;
      }
    }
  })();

  return bonusInFlight;
}

export const useCreditStore = create<CreditStoreState>((set, get) => ({
  balance: null,
  teamBalance: null,
  designFirmId: null,
  hideTeamBalance: false,
  isLoading: false,
  error: null,

  fetchBalance: async (force = false, uid?: string) => {
    set({ isLoading: true, ...(force ? { error: null } : {}) });
    try {
      const data = await callCreditsApi<{
        ok: boolean;
        balance: {
          stripeCredits: number;
          freePersonalCredits: number;
          combinedBalance: number;
          lastUpdated?: number | null;
          isLegacyUser?: boolean;
        };
      }>(`/api/credits/wallet${force ? "?force=true" : ""}`);

      if (!data?.ok) throw new Error("Failed to load wallet");

      set({
        balance: {
          totalBalance: data.balance.combinedBalance,
          stripeBalance: data.balance.stripeCredits,
          freeBalance: data.balance.freePersonalCredits,
          cachedUnbilledUsage: 0,
          lastUpdated: data.balance.lastUpdated ?? null,
          isLegacyUser: data.balance.isLegacyUser ?? false,
        },
        hideTeamBalance: false,
        isLoading: false,
        error: null,
      });

      // Trigger signup bonus (deduped, fire-and-forget).
      // If granted, the Firestore listener in subscribeToBalance picks up the updated balance.
      const resolvedUid = uid ?? currentUid;
      if (resolvedUid) {
        checkSignupBonus(resolvedUid);
      }
    } catch (error: unknown) {
      console.error("[CreditStore] fetchBalance error:", error);
      set({
        isLoading: false,
        error: getErrorMessage(error, "Failed to load balance"),
      });
    }
  },

  fetchTeamBalance: async (designFirmId: string, force = false) => {
    set({ isLoading: true, designFirmId, ...(force ? { error: null } : {}) });
    try {
      const data = await callCreditsApi<{
        ok: boolean;
        balance: {
          stripeCredits: number;
          lastUpdated?: number | null;
          isLegacyUser?: boolean;
        };
      }>(
        `/api/credits/team/${designFirmId}/wallet${force ? "?force=true" : ""}`,
      );

      if (!data?.ok) throw new Error("Failed to load team wallet");

      set({
        teamBalance: {
          totalBalance: data.balance.stripeCredits,
          stripeBalance: data.balance.stripeCredits,
          freeBalance: 0,
          cachedUnbilledUsage: 0,
          lastUpdated: data.balance.lastUpdated ?? null,
          isLegacyUser: data.balance.isLegacyUser ?? false,
        },
        hideTeamBalance: false,
        isLoading: false,
        error: null,
      });
    } catch (error: unknown) {
      console.error("[CreditStore] fetchTeamBalance error:", error);
      const rawMessage =
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : "";
      let parsedMessage = rawMessage;
      if (rawMessage.startsWith("{")) {
        try {
          const parsed = JSON.parse(rawMessage);
          parsedMessage =
            typeof parsed?.error === "string" ? parsed.error : rawMessage;
        } catch {
          parsedMessage = rawMessage;
        }
      }
      if (parsedMessage === "TEAM_STRIPE_CUSTOMER_NOT_CONFIGURED") {
        set({
          teamBalance: null,
          hideTeamBalance: true,
          isLoading: false,
          error: null,
        });
        return;
      }
      set({
        isLoading: false,
        error: parsedMessage || "Failed to load team balance",
      });
    }
  },

  checkSufficientCredits: (required: number) => {
    const { teamBalance, balance, designFirmId, hideTeamBalance } = get();

    // Team users: check team balance only
    if (designFirmId) {
      if (hideTeamBalance) return false;
      if (!teamBalance) return false;
      if (teamBalance.isLegacyUser) return true;
      return teamBalance.totalBalance >= required;
    }

    // Personal users: check personal balance
    if (!balance) return false;
    if (balance.isLegacyUser) return true;
    return balance.totalBalance >= required;
  },

  subscribeToBalance: (uid: string) => {
    if (personalUnsubscribe) {
      personalUnsubscribe();
      personalUnsubscribe = null;
    }

    const db = firebaseHelper.db;
    if (!db || !uid) return;

    currentUid = uid;
    set({ isLoading: true, error: null });

    personalUnsubscribe = onSnapshot(
      doc(db, "creditBalances", uid),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const lastUpdatedRaw = data.lastUpdated;
          const lastUpdated =
            lastUpdatedRaw && typeof lastUpdatedRaw.toMillis === "function"
              ? lastUpdatedRaw.toMillis()
              : lastUpdatedRaw ?? null;

          set({
            balance: {
              totalBalance:
                (data.stripeCredits ?? 0) +
                (data.freePersonalCredits ?? 0),
              stripeBalance: data.stripeCredits ?? 0,
              freeBalance: data.freePersonalCredits ?? 0,
              cachedUnbilledUsage: data.cachedUnbilledUsage ?? 0,
              lastUpdated,
              isLegacyUser: data.isLegacyUser ?? false,
            },
            hideTeamBalance: false,
            isLoading: false,
            error: null,
          });
        } else {
          set({ balance: null, isLoading: false });
        }
      },
      (error) => {
        console.error("[CreditStore] Balance listener error:", error);
        set({ isLoading: false, error: error.message });
      },
    );

    // Trigger signup bonus (deduped, fire-and-forget)
    checkSignupBonus(uid);
  },

  subscribeToTeamBalance: (designFirmId: string) => {
    if (teamUnsubscribe) {
      teamUnsubscribe();
      teamUnsubscribe = null;
    }

    const db = firebaseHelper.db;
    if (!db || !designFirmId) return;

    set({ isLoading: true, designFirmId, error: null });

    teamUnsubscribe = onSnapshot(
      doc(db, "creditBalances", `team_${designFirmId}`),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const lastUpdatedRaw = data.lastUpdated;
          const lastUpdated =
            lastUpdatedRaw && typeof lastUpdatedRaw.toMillis === "function"
              ? lastUpdatedRaw.toMillis()
              : lastUpdatedRaw ?? null;

          set({
            teamBalance: {
              totalBalance: data.stripeCredits ?? 0,
              stripeBalance: data.stripeCredits ?? 0,
              freeBalance: 0,
              cachedUnbilledUsage: data.cachedUnbilledUsage ?? 0,
              lastUpdated,
              isLegacyUser: data.isLegacyUser ?? false,
            },
            hideTeamBalance: false,
            isLoading: false,
            error: null,
          });
        } else {
          set({ teamBalance: null, hideTeamBalance: true, isLoading: false });
        }
      },
      (error) => {
        console.error("[CreditStore] Team balance listener error:", error);
        set({ isLoading: false, error: error.message });
      },
    );
  },

  unsubscribeAll: () => {
    if (personalUnsubscribe) {
      personalUnsubscribe();
      personalUnsubscribe = null;
    }
    if (teamUnsubscribe) {
      teamUnsubscribe();
      teamUnsubscribe = null;
    }
  },

  resetStore: () => {
    get().unsubscribeAll();
    currentUid = null;
    bonusCheckedForUid = null;
    bonusInFlightUid = null;
    bonusInFlight = null;
    set({
      balance: null,
      teamBalance: null,
      designFirmId: null,
      hideTeamBalance: false,
      isLoading: false,
      error: null,
    });
  },
}));
